"use client";

import { useState, useEffect } from "react";
import { useSession } from "next-auth/react";
import {
  saveCookiePreferences,
  getUserCookiePreferences,
} from "@/server/actions/core/cookies";

export type CookiePreferences = {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
};

export const defaultPreferences: CookiePreferences = {
  necessary: true,
  analytics: false,
  marketing: false,
};

export function useCookieConsent() {
  const [preferences, setPreferences] =
    useState<CookiePreferences>(defaultPreferences);
  const [loading, setLoading] = useState(true);
  const { data: session } = useSession();

  useEffect(() => {
    const loadPreferences = async () => {
      setLoading(true);
      try {
        // For authenticated users, check database first
        if (session?.user) {
          const dbConsent = await getUserCookiePreferences();
          if (dbConsent.success && dbConsent.data) {
            const { necessary, analytics, marketing } = dbConsent.data;
            const prefs: CookiePreferences = {
              necessary,
              analytics,
              marketing,
            };
            setPreferences(prefs);
            localStorage.setItem(
              "cookie-consent-status",
              JSON.stringify(prefs)
            );
          } else {
            // No consent in database, check localStorage
            const localConsent = localStorage.getItem("cookie-consent-status");
            if (localConsent) {
              setPreferences(JSON.parse(localConsent));
            } else {
              setPreferences(defaultPreferences);
            }
          }
        } else {
          // For non-authenticated users, check localStorage only
          const localConsent = localStorage.getItem("cookie-consent-status");
          if (localConsent) {
            setPreferences(JSON.parse(localConsent));
          }
        }
      } catch (error) {
        console.error("Failed to load cookie preferences:", error);
        // On error, use default preferences
        setPreferences(defaultPreferences);
      } finally {
        setLoading(false);
      }
    };

    loadPreferences();
  }, [session]);

  const acceptAll = async () => {
    const newPreferences: CookiePreferences = {
      necessary: true,
      analytics: true,
      marketing: true,
    };

    try {
      const result = await saveCookiePreferences({
        ...newPreferences,
        userId: session?.user?.id || "",
        id: "", // Server will generate this
        created: new Date(),
        updated: new Date(),
      });

      if (result.success) {
        setPreferences(newPreferences);
        localStorage.setItem(
          "cookie-consent-status",
          JSON.stringify(newPreferences)
        );
        return true;
      }
      return false;
    } catch (error) {
      console.error("Failed to save cookie preferences:", error);
      return false;
    }
  };

  const rejectAll = async () => {
    const newPreferences: CookiePreferences = {
      necessary: true,
      analytics: false,
      marketing: false,
    };

    try {
      const result = await saveCookiePreferences({
        ...newPreferences,
        userId: session?.user?.id || "",
        id: "", // Server will generate this
        created: new Date(),
        updated: new Date(),
      });

      if (result.success) {
        setPreferences(newPreferences);
        localStorage.setItem(
          "cookie-consent-status",
          JSON.stringify(newPreferences)
        );
        return true;
      }
      return false;
    } catch (error) {
      console.error("Failed to save cookie preferences:", error);
      return false;
    }
  };

  const updatePreferences = async (newPreferences: CookiePreferences) => {
    try {
      const result = await saveCookiePreferences({
        ...newPreferences,
        userId: session?.user?.id || "",
        id: "", // Server will generate this
        created: new Date(),
        updated: new Date(),
      });

      if (result.success) {
        setPreferences(newPreferences);
        localStorage.setItem(
          "cookie-consent-status",
          JSON.stringify(newPreferences)
        );
        return true;
      }
      return false;
    } catch (error) {
      console.error("Failed to save cookie preferences:", error);
      return false;
    }
  };

  const hasConsent =
    typeof window !== "undefined"
      ? Boolean(localStorage.getItem("cookie-consent-status"))
      : false;

  return {
    preferences,
    loading,
    hasConsent,
    acceptAll,
    rejectAll,
    updatePreferences,
  };
}
